/**
 * Edge Skills Warehouse · runtime F1 compose (function OTA ≠ board OTA).
 *
 * Soft≠Live:
 *  - Catalog = operator product surface (market only).
 *  - Running/Staged paint from device runtime telemetry only — never capability claimed/verified.
 *  - Install/activate/remove via runtime command+ACK (one inflight job per device).
 *  - Board OTA never ships from this desk (superadmin Fleet Firmware only).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, LayoutGroup } from "framer-motion";
import { html, Ico, Field, Input, CraftSelect, Alert, KV, go, M, SPRING } from "./ui.js";
import { path, useApi, useAction } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { DEVICE_TYPES } from "./edge-data.js";
import { isLocalHost } from "./pathseal.js";
import {
  edgeSkillCatalogListPath,
  edgeSkillCatalogStatusPath,
  edgeSkillCatalogStatusViewOf,
  edgeSkillCatalogViewOf,
  edgeSkillInstallAllowed,
  resolveEdgeSkillCatalogRows,
} from "./machine-edge-skills-catalog.js";
import {
  edgeSkillRuntimeStatusPath,
  edgeSkillRuntimeStatusViewOf,
  edgeSkillRuntimeDevicePath,
  edgeSkillRuntimeDeviceViewOf,
  edgeSkillRuntimeDeviceJobsPath,
  edgeSkillRuntimeJobsViewOf,
  edgeSkillRuntimeArtifactsPath,
  edgeSkillRuntimeArtifactsViewOf,
  edgeSkillRuntimeInstallPath,
  edgeSkillRuntimeRailsOf,
  edgeSkillRuntimeInstallGateOf,
  edgeSkillRuntimeArtifactForSkill,
  edgeSkillRuntimeInventorySay,
  edgeSkillRuntimeOpsAllowed,
  edgeSkillRuntimeViewOnly,
  edgeSkillRuntimeRunCommand,
  edgeSkillRuntimeRunPackSequential,
  EDGE_SKILL_RUNTIME_INSTALL_REFUSAL,
} from "./machine-edge-skills-runtime.js";
import { confirmDeviceCommand } from "./device-command-gate.js";
import { boardLoopViewOf, edgeWaveHonestyOf } from "./edgeos-live.js";
import { boardLabelSay } from "./operator-say.js";

/** Family sliding pill — one shared layout id only. */
const FAM_PILL = { type: "spring", stiffness: 480, damping: 34, mass: 0.62 };
/** Sticky compose / overlays · short tween, not layout thrash. */
const PANEL = { type: "tween", duration: 0.2, ease: [0.2, 0.0, 0.18, 1] };
const ENTER = { type: "spring", stiffness: 420, damping: 30, mass: 0.7 };

/** Product warehouse — function OTA skills only (never board firmware). */
export const EDGE_SKILL_WAREHOUSE = [
  {
    id: "sk.snapshot",
    label: "Snapshot still",
    family: "Sense",
    wave: "W12",
    ota: "function",
    bus: "registry",
    caps: ["snapshot"],
    budgetKiB: 8,
    risk: "low",
    dualHuman: false,
    forClass: ["edge_ai_node", "camera_hik", "camera_dahua", "camera_generic"],
    say: "On-demand JPEG still — preferred ladder step.",
  },
  {
    id: "sk.video",
    label: "Video stream name",
    family: "Sense",
    wave: "W12",
    ota: "function",
    bus: "registry",
    caps: ["video"],
    budgetKiB: 32,
    risk: "low",
    dualHuman: false,
    forClass: ["edge_ai_node", "camera_hik", "camera_dahua"],
    say: "Continuous stream capability claim · remux via μDock.",
  },
  {
    id: "sk.night",
    label: "Night vision",
    family: "Sense",
    wave: "W12",
    ota: "function",
    bus: "registry",
    caps: ["night_vision"],
    budgetKiB: 12,
    risk: "low",
    dualHuman: false,
    forClass: ["edge_ai_node", "camera_hik", "camera_dahua"],
    say: "IR / low-light path claim.",
  },
  {
    id: "sk.thermal",
    label: "Thermal plane",
    family: "Sense",
    wave: "W12",
    ota: "function",
    bus: "registry",
    caps: ["thermal"],
    budgetKiB: 24,
    risk: "med",
    dualHuman: false,
    forClass: ["edge_ai_node", "camera_hik"],
    conflicts: [],
    say: "Thermal imager seat.",
  },
  {
    id: "sk.ptz",
    label: "PTZ optics",
    family: "Sense",
    wave: "W12",
    ota: "function",
    bus: "registry",
    caps: ["ptz"],
    budgetKiB: 16,
    risk: "med",
    dualHuman: true,
    forClass: ["camera_hik", "camera_dahua"],
    say: "Pan / tilt / zoom execute claim.",
  },
  {
    id: "sk.locate",
    label: "Locate · FTM seat",
    family: "Sense",
    wave: "W5",
    ota: "function",
    bus: "capsule_pending",
    caps: [],
    budgetKiB: 48,
    risk: "low",
    dualHuman: false,
    forClass: ["edge_ai_node", "croc_sentinel"],
    say: "±10 m FTM multi-anchor report skill · Capsule function OTA pending.",
  },
  {
    id: "sk.cmo.observe",
    label: "CMO observe",
    family: "Compute",
    wave: "W3",
    ota: "function",
    bus: "capsule_pending",
    caps: [],
    budgetKiB: 1400,
    risk: "med",
    dualHuman: false,
    forClass: ["edge_ai_node"],
    refuseOn: ["alarm_disk"],
    say: "Observation-only Capsule · never score people · never actuators.",
  },
  {
    id: "sk.runtime.pool",
    label: "Tensor pool lease",
    family: "Compute",
    wave: "W4",
    ota: "function",
    bus: "capsule_pending",
    caps: [],
    budgetKiB: 64,
    risk: "low",
    dualHuman: false,
    forClass: ["edge_ai_node"],
    say: "Runtime budget policy · dual-core isolation rules.",
  },
  {
    id: "sk.mesh.peer",
    label: "Mesh peer pulse",
    family: "Path",
    wave: "W10",
    ota: "function",
    bus: "capsule_pending",
    caps: [],
    budgetKiB: 20,
    risk: "med",
    dualHuman: false,
    forClass: ["edge_ai_node", "croc_sentinel"],
    say: "Event codes + priming · no model weights on 802.15.4.",
  },
  {
    id: "sk.borrow.serve",
    label: "Skill serve (borrow)",
    family: "Path",
    wave: "W2",
    ota: "function",
    bus: "capsule_pending",
    caps: [],
    budgetKiB: 40,
    risk: "med",
    dualHuman: false,
    forClass: ["edge_ai_node"],
    say: "Serve sibling REQUEST RPC · never lend whole weights.",
  },
  {
    id: "sk.siren",
    label: "Siren output",
    family: "Actuate",
    wave: "W8",
    ota: "function",
    bus: "registry",
    caps: ["siren"],
    budgetKiB: 8,
    risk: "high",
    dualHuman: true,
    forClass: ["croc_sentinel", "edge_ai_node"],
    say: "Audible execute · dual human on deploy.",
  },
  {
    id: "sk.speaker",
    label: "Speaker out",
    family: "Actuate",
    wave: "W8",
    ota: "function",
    bus: "registry",
    caps: ["speaker"],
    budgetKiB: 8,
    risk: "high",
    dualHuman: true,
    forClass: ["croc_sentinel"],
    say: "One-way audio out.",
  },
  {
    id: "sk.door",
    label: "Door release",
    family: "Actuate",
    wave: "W8",
    ota: "function",
    bus: "registry",
    caps: ["door"],
    budgetKiB: 6,
    risk: "high",
    dualHuman: true,
    forClass: ["croc_sentinel"],
    say: "Door execute · dual human.",
  },
  {
    id: "sk.gate",
    label: "Vehicle gate",
    family: "Actuate",
    wave: "W8",
    ota: "function",
    bus: "registry",
    caps: ["gate"],
    budgetKiB: 6,
    risk: "high",
    dualHuman: true,
    forClass: ["croc_sentinel"],
    say: "Gate execute · dual human.",
  },
  {
    id: "sk.talk",
    label: "Two-way talk",
    family: "Actuate",
    wave: "W8",
    ota: "function",
    bus: "registry",
    caps: ["talk"],
    budgetKiB: 12,
    risk: "med",
    dualHuman: false,
    forClass: ["human_responder", "croc_sentinel"],
    say: "Talk path claim.",
  },
  {
    id: "sk.move",
    label: "Mobile relocate",
    family: "Actuate",
    wave: "W8",
    ota: "function",
    bus: "registry",
    caps: ["move"],
    budgetKiB: 10,
    risk: "med",
    dualHuman: false,
    forClass: ["human_responder"],
    say: "Resource can relocate itself.",
  },
  {
    id: "sk.cold.cache",
    label: "Cold→flash promote",
    family: "Store",
    wave: "W11",
    ota: "function",
    bus: "capsule_pending",
    caps: [],
    budgetKiB: 0,
    risk: "med",
    dualHuman: true,
    forClass: ["edge_ai_node"],
    say: "Promote Capsule A→flash cache · never hot-path TF.",
  },
];

export const EDGE_SKILL_PRESETS = [
  {
    id: "pack.edge_eye",
    label: "Edge eye (AI node)",
    skills: ["sk.snapshot", "sk.video", "sk.night", "sk.cmo.observe", "sk.runtime.pool"],
    deviceType: "edge_ai_node",
  },
  {
    id: "pack.alarm_panel",
    label: "Alarm panel",
    skills: ["sk.siren", "sk.speaker"],
    deviceType: "croc_sentinel",
  },
  {
    id: "pack.cam_ptz",
    label: "Camera + PTZ",
    skills: ["sk.snapshot", "sk.video", "sk.night", "sk.ptz"],
    deviceType: "camera_hik",
  },
  {
    id: "pack.mesh_ai",
    label: "AI + mesh serve",
    skills: ["sk.snapshot", "sk.cmo.observe", "sk.mesh.peer", "sk.borrow.serve"],
    deviceType: "edge_ai_node",
  },
];

export const SKILL_BUDGET_KIB = 2048;

/** Pure: skill by id. */
export function edgeSkillOf(id, warehouse) {
  const rows = Array.isArray(warehouse) && warehouse.length ? warehouse : EDGE_SKILL_WAREHOUSE;
  return rows.find((s) => s.id === id) || EDGE_SKILL_WAREHOUSE.find((s) => s.id === id) || null;
}

/** Pure: unique cap slugs for selected skills (registry Live write). */
export function capSlugsFromSkills(ids, warehouse) {
  const out = new Set();
  for (const id of ids || []) {
    const sk = edgeSkillOf(id, warehouse);
    if (!sk) continue;
    for (const c of sk.caps || []) out.add(c);
  }
  return [...out].sort();
}

/**
 * Pure: compose selected skills into a function-OTA pack.
 * @returns {{
 *   ok:boolean, errors:string[], warnings:string[],
 *   skills:object[], capsLive:string[], capsulePending:string[],
 *   budgetKiB:number, dualHuman:boolean, highRisk:boolean
 * }}
 */
export function composeSkillPack(selectedIds, opts) {
  const o = opts && typeof opts === "object" ? opts : {};
  const deviceType = String(o.deviceType || "").trim().toLowerCase();
  const budgetMax = Number.isFinite(Number(o.budgetMaxKiB))
    ? Number(o.budgetMaxKiB)
    : SKILL_BUDGET_KIB;
  const warehouse = Array.isArray(o.warehouse) && o.warehouse.length ? o.warehouse : EDGE_SKILL_WAREHOUSE;
  const ids = [...new Set((selectedIds || []).map((x) => String(x || "").trim()).filter(Boolean))];
  const errors = [];
  const warnings = [];
  if (!ids.length) errors.push("Select at least one skill");

  const skills = [];
  let budget = 0;
  let dualHuman = false;
  let highRisk = false;
  for (const id of ids) {
    const sk = edgeSkillOf(id, warehouse);
    if (!sk) {
      errors.push("Unknown skill · " + id);
      continue;
    }
    if (sk.ota !== "function") {
      errors.push(sk.id + " is not a capability pack (board updates stay separate)");
      continue;
    }
    if (deviceType && Array.isArray(sk.forClass) && sk.forClass.length
      && !sk.forClass.includes(deviceType)) {
      warnings.push(sk.label + " not typical for type " + deviceType);
    }
    if (deviceType === "croc_sentinel" && sk.refuseOn && sk.refuseOn.includes("alarm_disk")) {
      errors.push(sk.label + " refuse · alarm disk never runs AI class skills");
    }
    if (sk.dualHuman) dualHuman = true;
    if (sk.risk === "high") highRisk = true;
    budget += Number(sk.budgetKiB) || 0;
    skills.push(sk);
  }

  if (budget > budgetMax) {
    errors.push("PSRAM budget " + budget + " KiB > " + budgetMax + " KiB ceiling");
  }

  /* soft conflict: cmo + alarm-class already handled */
  const capsLive = capSlugsFromSkills(ids, warehouse);
  const capsulePending = skills
    .filter((s) => s.bus === "capsule_pending")
    .map((s) => s.id);

  return {
    ok: errors.length === 0 && skills.length > 0,
    errors,
    warnings,
    skills,
    capsLive,
    capsulePending,
    budgetKiB: budget,
    dualHuman: dualHuman || highRisk,
    highRisk,
    functionOta: true,
    boardOta: false,
  };
}

/** Pure: families for filter UI. */
export function skillFamiliesOf() {
  return [...new Set(EDGE_SKILL_WAREHOUSE.map((s) => s.family))];
}

/**
 * @deprecated Legacy cap-slug paint — not used for runtime F1 on-device state.
 * Running/Staged use edgeSkillRuntimeRailsOf telemetry projection instead.
 */
export function skillInstallPaintOf(skill, capsView) {
  const v = capsView && typeof capsView === "object" ? capsView : {};
  const claimed = v.claimedSet || new Set(v.claimed || []);
  const verified = v.verifiedSet || new Set(v.verified || []);
  const caps = skill && Array.isArray(skill.caps) ? skill.caps : [];
  if (!caps.length) {
    return { state: "capsule", label: "Capsule · pending bus" };
  }
  const allClaimed = caps.every((c) => claimed.has(c));
  const allVerified = caps.every((c) => verified.has(c));
  if (allVerified) return { state: "verified", label: "Live verified" };
  if (allClaimed) return { state: "claimed", label: "Live claimed" };
  return { state: "empty", label: "not on device" };
}

function safePath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__devices") return "/devices";
    if (op === "get__devices_capabilities") return "/devices/capabilities";
    if (op === "get__devices_p_capabilities") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/devices/" + encodeURIComponent(id) + "/capabilities" : "";
    }
    if (op === "get__devices_p_board-loop") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/devices/" + encodeURIComponent(id) + "/board-loop" : "";
    }
    if (op === "post__devices_p_capabilities") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/devices/" + encodeURIComponent(id) + "/capabilities" : "";
    }
    return "";
  }
}

/** Map Live catalog row → warehouse card shape for compose UI. */
function warehouseRowOf(item) {
  if (!item || typeof item !== "object") return null;
  const id = String(item.id || item.skillId || "").trim();
  if (!id) return null;
  const caps = item.requiredCapabilities || item.caps || [];
  return {
    id,
    label: String(item.label || id).trim(),
    family: String(item.kind || "Skill").trim(),
    wave: String(item.version || "Live").trim(),
    ota: "function",
    bus: caps.length ? "registry" : "capsule_pending",
    caps,
    budgetKiB: Number(item.sizeKiB) || 0,
    risk: "med",
    dualHuman: false,
    forClass: [],
    say: String(item.say || "").trim() || "Live edge catalog skill",
    softPreview: item.softPreview === true,
    catalogHonesty: item.softPreview ? "soft_preview" : "live",
  };
}

/**
 * Skills warehouse desk.
 * @param {{
 *   mode?: 'edge'|'device',
 *   deviceId?: string,
 *   toast?: function,
 *   seat?: object,
 * }} props
 */
/** Shared truthful capability seat projection for Skills and in-place Machine workspaces. */
export function skillsSeatOf(status) {
  const rail = (status && status.verb_matrix && status.verb_matrix.rail_a) || {};
  return {
    surveyAllowed: rail.visible !== false,
    commandAllowed: !!rail.execute_scoped,
    packagePushAllowed: !!rail.execute_scoped,
    executeGlobal: !!rail.execute_global,
    campaignSor: !!rail.campaign_sor,
  };
}

export function EdgeSkillsWarehouseDesk({ mode, deviceId: fixedId, toast, seat }) {
  const m = mode === "device" ? "device" : "edge";
  const local = isLocalHost();
  const surveyOn = seat ? seat.surveyAllowed === true : false;
  const cmdOn = seat ? seat.commandAllowed === true : false;
  const pushOk = seat
    ? (seat.packagePushAllowed === true || (seat.packagePushAllowed == null && seat.commandAllowed === true))
    : false;

  const [family, setFamily] = useState("all");
  const [picked, setPicked] = useState(() => new Set());
  const [focusId, setFocusId] = useState("");
  const [deviceId, setDeviceId] = useState(String(fixedId || "").trim());
  const [deviceType, setDeviceType] = useState("edge_ai_node");
  const [confirmA, setConfirmA] = useState(false);
  const [confirmB, setConfirmB] = useState(false);
  const [manifestNote, setManifestNote] = useState("");
  const act = useAction();
  const pendingIdemRef = useRef({});

  useEffect(() => {
    if (fixedId) setDeviceId(String(fixedId).trim());
  }, [fixedId]);

  const reg = useApi(
    surveyOn && m === "edge" ? safePath("get__devices", { query: { limit: 80 } }) : null,
  );
  const devices = useMemo(() => {
    const rows = (reg.data && (reg.data.items || reg.data.devices)) || [];
    if (!Array.isArray(rows)) return [];
    const mapped = rows
      .map((r) => {
        if (!r) return null;
        const id = String(r.device_id || r.id || "").trim();
        if (!id) return null;
        return {
          id,
          label: boardLabelSay(r, id),
          online: !!(r.is_online === true || r.online === true),
        };
      })
      .filter(Boolean);
    const seen = new Set();
    const unique = [];
    for (const d of mapped) {
      if (seen.has(d.id)) continue;
      seen.add(d.id);
      unique.push(d);
    }
    return unique;
  }, [reg.data]);
  useEffect(() => {
    if (fixedId || deviceId || m !== "edge") return;
    const prefer = devices.find((d) => d.online) || devices[0];
    if (prefer && prefer.id) setDeviceId(prefer.id);
  }, [devices, deviceId, fixedId, m]);

  const loopApi = useApi(
    surveyOn && deviceId
      ? safePath("get__devices_p_board-loop", { pathParams: [deviceId] }) || null
      : null,
  );
  const skillFacts = useMemo(() => edgeWaveHonestyOf(boardLoopViewOf(loopApi.data)), [loopApi.data]);

  const runtimeStatusApi = useApi(surveyOn ? edgeSkillRuntimeStatusPath() : null);
  const runtimeStatus = useMemo(
    () => edgeSkillRuntimeStatusViewOf(runtimeStatusApi.data),
    [runtimeStatusApi.data],
  );
  const runtimeProductOn = runtimeStatus.productEnabled === true;

  const runtimeDeviceApi = useApi(
    surveyOn && deviceId && runtimeProductOn ? edgeSkillRuntimeDevicePath(deviceId) : null,
  );
  const runtimeView = useMemo(
    () => edgeSkillRuntimeDeviceViewOf(runtimeDeviceApi.data),
    [runtimeDeviceApi.data],
  );
  const runtimeOpsOn = edgeSkillRuntimeOpsAllowed(runtimeStatus, runtimeView);
  const runtimeViewOnly = edgeSkillRuntimeViewOnly(runtimeStatus, runtimeView);
  const installAllowed = edgeSkillInstallAllowed(local, runtimeStatus, runtimeView);
  const runtimeJobsApi = useApi(
    surveyOn && deviceId && runtimeProductOn
      ? edgeSkillRuntimeDeviceJobsPath(deviceId, { limit: 50 })
      : null,
  );
  const runtimeJobsView = useMemo(
    () => edgeSkillRuntimeJobsViewOf(runtimeJobsApi.data),
    [runtimeJobsApi.data],
  );
  const runtimeArtifactsApi = useApi(
    surveyOn && runtimeProductOn ? edgeSkillRuntimeArtifactsPath() : null,
  );
  const runtimeArtifactsView = useMemo(
    () => edgeSkillRuntimeArtifactsViewOf(runtimeArtifactsApi.data),
    [runtimeArtifactsApi.data],
  );

  const catalogStatusApi = useApi(surveyOn ? edgeSkillCatalogStatusPath() : null);
  const catalogStatus = useMemo(
    () => edgeSkillCatalogStatusViewOf(catalogStatusApi.data),
    [catalogStatusApi.data],
  );
  const catalogListApi = useApi(
    surveyOn && catalogStatus.listAllowed
      ? edgeSkillCatalogListPath({ deviceId: deviceId || undefined })
      : null,
  );
  const catalogView = useMemo(
    () => edgeSkillCatalogViewOf(catalogListApi.data),
    [catalogListApi.data],
  );
  const catalogPack = useMemo(
    () => resolveEdgeSkillCatalogRows({
      statusView: catalogStatus,
      catalogView,
      catalogError: catalogListApi.error,
      local,
    }),
    [catalogStatus, catalogView, catalogListApi.error, local],
  );
  const runtimeRails = useMemo(
    () => edgeSkillRuntimeRailsOf({
      runtimeView,
      catalogRows: catalogPack.rows,
      jobsView: runtimeJobsView,
    }),
    [runtimeView, catalogPack.rows, runtimeJobsView],
  );
  const runtimePaintBySkill = useMemo(() => {
    const map = new Map();
    for (const s of runtimeRails.running) {
      map.set(s.skillId, { state: "running", label: "Running · telemetry" });
    }
    for (const s of runtimeRails.staged) {
      map.set(s.skillId, { state: "staged", label: "Staged · telemetry" });
    }
    return map;
  }, [runtimeRails.running, runtimeRails.staged]);
  const inventorySay = edgeSkillRuntimeInventorySay(runtimeRails.inventory);
  const shelfSource = useMemo(() => {
    const liveRows = catalogPack.rows.map(warehouseRowOf).filter(Boolean);
    if (liveRows.length) {
      return { rows: liveRows, label: catalogPack.label, honesty: catalogPack.honesty };
    }
    return {
      rows: [],
      label: catalogPack.label || "Catalog unavailable · read-only",
      honesty: catalogPack.unavailable ? "unavailable" : catalogPack.honesty,
    };
  }, [catalogPack]);

  const families = useMemo(
    () => [...new Set(shelfSource.rows.map((s) => s.family))],
    [shelfSource.rows],
  );
  const visible = useMemo(() => {
    return shelfSource.rows.filter(
      (s) => family === "all" || s.family === family,
    );
  }, [family, shelfSource.rows]);

  const pack = useMemo(
    () => composeSkillPack([...picked], {
      deviceType,
      budgetMaxKiB: SKILL_BUDGET_KIB,
      warehouse: shelfSource.rows,
    }),
    [picked, deviceType, shelfSource.rows],
  );

  const toggle = (id) => {
    setPicked((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
    setFocusId((cur) => (cur === id ? id : id));
    setConfirmA(false);
    setConfirmB(false);
  };

  const applyPreset = (preset) => {
    setPicked(new Set(preset.skills || []));
    if (preset.deviceType) setDeviceType(preset.deviceType);
    setConfirmA(false);
    setConfirmB(false);
    setFocusId((preset.skills && preset.skills[0]) || "");
    toast && toast("Preset loaded · " + preset.label, "go");
  };

  const clearPick = () => {
    setPicked(new Set());
    setFocusId("");
    setConfirmA(false);
    setConfirmB(false);
  };

  const reloadRuntime = () => {
    runtimeDeviceApi.reload && runtimeDeviceApi.reload();
    runtimeJobsApi.reload && runtimeJobsApi.reload();
    runtimeArtifactsApi.reload && runtimeArtifactsApi.reload();
  };

  const readRuntimeJobs = async () => act.run(
    edgeSkillRuntimeDeviceJobsPath(deviceId, { limit: 50 }),
    { method: "GET" },
  );
  const readRuntimeDevice = async () => act.run(
    edgeSkillRuntimeDevicePath(deviceId),
    { method: "GET" },
  );

  const runtimeGateCtx = (skillId, catalogRow, artifact) => ({
    statusView: runtimeStatus,
    runtimeView,
    inventory: runtimeView.inventory,
    skillId,
    catalogRow,
    artifact,
  });

  const dispatchFunctionOta = async () => {
    if (!installAllowed) {
      toast && toast(EDGE_SKILL_RUNTIME_INSTALL_REFUSAL, "warn");
      return;
    }
    if (!cmdOn || !pushOk) {
      toast && toast("Skill update is closed for this seat", "warn");
      return;
    }
    if (!deviceId) {
      toast && toast("Pick a target Edge node", "warn");
      return;
    }
    if (!pack.ok) {
      toast && toast(pack.errors[0] || "Pack invalid", "warn");
      return;
    }
    if (pack.dualHuman && !(confirmA && confirmB)) {
      toast && toast("Dual human confirm required for high-risk / execute skills", "warn");
      return;
    }
    if (!runtimeOpsOn) {
      toast && toast(EDGE_SKILL_RUNTIME_INSTALL_REFUSAL, "warn");
      return;
    }
    const ok = await confirmDeviceCommand({
      kind: "install",
      targets: [{ id: deviceId, label: deviceId }],
      detail: "Sequential runtime pack · one skill at a time · each waits for job ACK + telemetry.",
      confirmLabel: "Install pack",
    });
    if (!ok) return;

    const packSkills = pack.skills.map((sk) => {
      const catalogRow = catalogPack.rows.find((r) => String(r.skillId || r.id) === sk.id) || null;
      const artifact = edgeSkillRuntimeArtifactForSkill(runtimeArtifactsView, sk.id);
      return {
        skillId: sk.id,
        label: sk.label,
        artifactId: artifact && artifact.artifactId,
        catalogRow,
        artifact,
      };
    });

    const packOut = await edgeSkillRuntimeRunPackSequential({
      skills: packSkills,
      runOne: async (sk) => {
        const gate = edgeSkillRuntimeInstallGateOf(
          runtimeGateCtx(sk.skillId, sk.catalogRow, sk.artifact),
        );
        if (!gate.allowed) {
          return { ok: false, say: gate.label };
        }
        return edgeSkillRuntimeRunCommand({
          action: "install",
          skillId: sk.skillId,
          artifactId: sk.artifactId,
          idempotencyStore: pendingIdemRef.current,
          expectedTelemetryStates: ["active", "staged"],
          dispatch: async ({ headers, body }) => act.run(edgeSkillRuntimeInstallPath(deviceId), {
            method: "POST",
            headers,
            body,
          }),
          readJobs: readRuntimeJobs,
          readRuntime: readRuntimeDevice,
        });
      },
    });

    setManifestNote("");
    reloadRuntime();
    if (!packOut.ok) {
      toast && toast(packOut.say, "bad");
      return;
    }
    const last = packOut.completed.length
      ? packOut.completed[packOut.completed.length - 1].result
      : null;
    if (last && last.ok && !last.telemetryObserved) {
      toast && toast(last.say, "warn");
      return;
    }
    toast && toast(packOut.say, "go");
  };

  const famKey = family || "all";
  const focusSk = focusId ? edgeSkillOf(focusId, shelfSource.rows) : null;
  const budgetPct = Math.min(100, Math.round((pack.budgetKiB / SKILL_BUDGET_KIB) * 100));
  const dispatchDisabled = !pack.ok || !deviceId || act.busy || !cmdOn || !pushOk || !installAllowed
    || (pack.dualHuman && !(confirmA && confirmB));

  /** Toggle pack only — detail paints in the rail (no bottom sticky dock). */
  const onCard = (id) => {
    toggle(id);
    setFocusId(id);
  };

  return html`<section
    className="edge-skills edge-wave edge-desk-body edge-skills--kinetic edge-skills--cards edge-skills--v500"
    data-craft="v500"
    data-skills-ia="warehouse-v3"
    data-cmc-skills="function-ota"
    data-kinetic="1"
    data-cards="1"
    data-scroll="sheet"
    data-bench="1">
    ${act.envelope
      ? html`<${KV} k="Last action" v=${envelopeSummary(act.envelope) || "—"} tone=${act.envelope.ok ? "hi" : "warn"}/>`
      : null}
    <header className="edge-wave__hd" data-nested="1">
      <div>
        <p className="edge-wave__k">Edge · Skills · sole Compose</p>
        <h2 className="edge-wave__title">Function OTA packs</h2>
        <p className="note">${shelfSource.label}${installAllowed ? "" : " · " + EDGE_SKILL_RUNTIME_INSTALL_REFUSAL}</p>
        ${runtimeProductOn && deviceId
          ? html`<p className="note">${inventorySay}${runtimeViewOnly ? " · look only — this seat cannot install yet" : ""}</p>`
          : null}
        ${skillFacts.skill
          ? html`<p className="note">Board loop · ${skillFacts.skill}${skillFacts.skillMqttClosed ? " · link closed" : ""} · no invented radio</p>`
          : null}
      </div>
    </header>

    ${catalogPack.unavailable && !local
      ? html`<${Alert} tone="warn" title="Edge skill catalog unavailable"
          say=${catalogPack.label + " · shelf is read-only until Live catalog is armed."}/>`
      : null}

    <${M.div}
      className="edge-skills__toolbar"
      initial=${{ opacity: 0, y: 8 }}
      animate=${{ opacity: 1, y: 0 }}
      transition=${ENTER}>
      <${LayoutGroup} id="edge-skills-fam-v500">
        <div className="edge-skills__filters" role="tablist" aria-label="Skill family">
          ${(["all", ...families]).map((f) => {
            const id = f;
            const lab = f === "all" ? "All" : f;
            const on = famKey === id;
            return html`<button
              key=${id}
              type="button"
              role="tab"
              className="edge-skills__fchip"
              data-on=${on ? "1" : "0"}
              aria-selected=${on ? "true" : "false"}
              onClick=${() => setFamily(id)}>
              ${on
                ? html`<${M.span}
                    layoutId="edge-skills-fam-pill-v500"
                    className="edge-skills__fpill"
                    transition=${FAM_PILL}
                    aria-hidden="true"
                  />`
                : null}
              <span className="edge-skills__flab">${lab}</span>
            </button>`;
          })}
        </div>
      <//>
      <div className="edge-skills__presetbar" role="group" aria-label="Presets">
        ${EDGE_SKILL_PRESETS.map((p) => html`<${M.button}
          key=${p.id}
          type="button"
          className="edge-skills__preset"
          whileHover=${{ y: -2 }}
          whileTap=${{ scale: 0.97 }}
          transition=${SPRING}
          onClick=${() => applyPreset(p)}>${p.label}<//>`)}
        <button type="button" className="edge-skills__preset edge-skills__preset--ghost"
          onClick=${clearPick}>Clear</button>
      </div>
    <//>

    <div className="edge-skills__bench">
      <div className="edge-skills__main">
        <div className="edge-skills__main-hd">
          <h3 className="edge-skills__catalog-title">Skill shelf</h3>
          <span className="edge-skills__catalog-meta">${visible.length} · ${shelfSource.honesty === "live" ? "Live catalog" : shelfSource.honesty === "soft_preview" ? "Soft preview" : "read-only"}</span>
        </div>
        <div className="edge-skills__shelf" role="list" key=${"shelf-" + famKey} aria-label="Skill shelf">
          ${visible.map((sk, i) => {
            const on = picked.has(sk.id);
            const paint = deviceId
              ? (runtimeOpsOn
                ? (runtimePaintBySkill.get(sk.id) || { state: "market", label: "Market · catalog" })
                : (local && shelfSource.honesty === "soft_preview"
                  ? { state: "soft", label: "Soft preview · not Live" }
                  : { state: "readonly", label: "Read-only · runtime off" }))
              : { state: "empty", label: "—" };
            return html`<${M.button}
              key=${sk.id}
              type="button"
              role="listitem"
              className="edge-skills__card"
              data-on=${on ? "1" : "0"}
              data-bus=${sk.bus}
              data-risk=${sk.risk}
              data-focus=${focusId === sk.id ? "1" : "0"}
              aria-pressed=${on ? "true" : "false"}
              initial=${{ opacity: 0, y: 12, scale: 0.97 }}
              animate=${{ opacity: 1, y: 0, scale: 1 }}
              transition=${{ ...ENTER, delay: Math.min(i * 0.025, 0.2) }}
              whileHover=${{ y: -4 }}
              whileTap=${{ scale: 0.985 }}
              onClick=${() => onCard(sk.id)}>
              <span className="edge-skills__card-glow" aria-hidden="true"/>
              <header className="edge-skills__card-hd">
                <b>${sk.label}</b>
                <em>${sk.wave}</em>
              </header>
              <p>${sk.say}</p>
              <div className="edge-skills__meta">
                <span>${sk.family}</span>
                <span>${sk.catalogHonesty === "soft_preview" ? "Soft" : sk.bus === "registry" ? "Live" : "Capsule"}</span>
                <span>${sk.budgetKiB} KiB</span>
                ${sk.dualHuman ? html`<span data-h="1">dual</span>` : null}
                ${deviceId ? html`<span data-st=${paint.state}>${paint.label}</span>` : null}
              </div>
              <${AnimatePresence}>
                ${on
                  ? html`<${M.span}
                      key="tick"
                      className="edge-skills__card-tick"
                      aria-hidden="true"
                      initial=${{ opacity: 0, scale: 0.86 }}
                      animate=${{ opacity: 1, scale: 1 }}
                      exit=${{ opacity: 0, scale: 0.9 }}
                      transition=${PANEL}>
                      <${Ico} d="check" w=${11} s=${{ strokeWidth: 2.4 }}/>
                    <//>`
                  : null}
              <//>
            <//>`;
          })}
        </div>
      </div>

      <aside className="edge-skills__rail" aria-label="Compose pack">
        <${M.div}
          className="edge-skills__rail-card"
          initial=${{ opacity: 0, x: 12 }}
          animate=${{ opacity: 1, x: 0 }}
          transition=${ENTER}>
          <header className="edge-skills__rail-hd">
            <div>
              <span className="edge-skills__zone-k">Compose</span>
              <b>Function pack</b>
            </div>
            <${M.span}
              className="edge-skills__pillcount"
              key=${"pc-" + picked.size}
              data-on=${picked.size ? "1" : "0"}
              initial=${{ scale: 0.9 }}
              animate=${{ scale: 1 }}
              transition=${SPRING}>${picked.size}</${M.span}>
          </header>

          <div className="edge-skills__budget" aria-hidden="true">
            <i style=${{ width: budgetPct + "%" }}/>
          </div>
          <p className="edge-skills__budget-lab">${pack.budgetKiB} / ${SKILL_BUDGET_KIB} KiB</p>

          <div className="edge-skills__picked">
            ${pack.skills.length
              ? pack.skills.map((s) => html`<button
                  key=${s.id}
                  type="button"
                  className="edge-skills__chip"
                  data-on=${focusId === s.id ? "1" : "0"}
                  onClick=${() => setFocusId(s.id)}
                  title=${s.label}>${s.label}</button>`)
              : html`<p className="edge-skills__empty">Select cards on the left to build a pack.</p>`}
          </div>

          <${AnimatePresence} mode="wait">
            ${focusSk
              ? html`<${M.div}
                  key=${"fx-" + focusSk.id}
                  className="edge-skills__focusline"
                  initial=${{ opacity: 0, y: 6 }}
                  animate=${{ opacity: 1, y: 0 }}
                  exit=${{ opacity: 0, y: -4 }}
                  transition=${PANEL}>
                  <b>${focusSk.label}</b>
                  <p>${focusSk.say}</p>
                <//>`
              : null}
          <//>

          ${pack.errors.map((e) => html`<${Alert} key=${e} tone="bad" title="Compose block" say=${e}/>`)}
          ${pack.warnings.map((w) => html`<p key=${w} className="note">${w}</p>`)}

          <div className="edge-skills__fields">
            ${m === "edge"
              ? html`<${Field} label="Target device">
                  <${CraftSelect}
                    value=${deviceId}
                    disabled=${!surveyOn}
                    allowEmpty
                    placeholder="Select…"
                    aria-label="Target device"
                    options=${devices.map((d) => ({
                      value: d.id,
                      label: `${d.label}${d.online ? "" : " · quiet"} (${d.id})`,
                    }))}
                    onChange=${(e) => setDeviceId(e.target.value)}/>
                <//>`
              : html`<${KV} k="Device" v=${deviceId || "—"}/>`}
            <${Field} label="Device type">
              <${CraftSelect}
                value=${deviceType}
                placeholder="Device type"
                aria-label="Device type"
                options=${DEVICE_TYPES.map((t) => ({ value: t.id, label: t.label }))}
                onChange=${(e) => setDeviceType(e.target.value)}/>
            <//>
            <${Field} label="Note">
              <${Input} placeholder="site week-12 · yard eyes" value=${manifestNote}
                onChange=${(e) => setManifestNote(e.target.value)}/>
            <//>
          </div>

          <${KV} k="Live caps" v=${pack.capsLive.join(", ") || "—"}/>
          <${KV} k="Capsule" v=${pack.capsulePending.join(", ") || "—"}/>
          <${KV} k="Dual human" v=${pack.dualHuman ? "required" : "no"}/>

          ${pack.dualHuman
            ? html`<div className="edge-skills__dual">
                <label className="edge-wave__check">
                  <input type="checkbox" checked=${confirmA}
                    onChange=${(e) => setConfirmA(!!e.target.checked)}/>
                  Operator A · wiring matches
                </label>
                <label className="edge-wave__check">
                  <input type="checkbox" checked=${confirmB}
                    onChange=${(e) => setConfirmB(!!e.target.checked)}/>
                  Operator B · second human
                </label>
              </div>`
            : null}

          <div className="btn-row edge-skills__acts">
            <p className="note">${installAllowed
              ? "One skill at a time. The board must confirm before it is on."
              : EDGE_SKILL_RUNTIME_INSTALL_REFUSAL}</p>
            <${M.button}
              type="button"
              className="btn btn--go btn--sm"
              disabled=${dispatchDisabled}
              whileHover=${dispatchDisabled ? undefined : { scale: 1.02 }}
              whileTap=${dispatchDisabled ? undefined : { scale: 0.97 }}
              transition=${SPRING}
              onClick=${dispatchFunctionOta}>
              <${Ico} d="check" w=${13}/> ${installAllowed ? "Dispatch runtime install" : "Install closed"}
              ${pack.skills.length ? " · " + pack.skills.length + " skill(s)" : ""}
            <//>
            <button type="button" className="btn btn--ghost btn--sm"
              disabled=${!deviceId || !surveyOn}
              onClick=${() => {
                reloadRuntime();
                toast && toast("Runtime + catalog re-read", "go");
              }}>
              <${Ico} d="refresh" w=${13}/> Re-read
            </button>
          </div>

          ${seat && seat.baseImageAllowed ? html`<div className="edge-skills__hand">
              <span>Board updates</span>
              <button type="button" className="edge-cap-hand__go"
                onClick=${() => go("/ota/base")}>Base image</button>
              <button type="button" className="edge-cap-hand__go"
                onClick=${() => go("/ota")}>Open OTA</button>
            </div>` : null}
        <//>
      </aside>
    </div>
  </section>`;
}

export default EdgeSkillsWarehouseDesk;
